import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AutoCareCta from "../components/AutoCareCta";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import PhotoUploader from "../components/PhotoUploader";
import VinBadge from "../components/VinBadge";
import InstantOffer from "../components/InstantOffer";
import { toast } from "sonner";
import { api as http } from "../lib/api";
import { useEffect } from "react";
import SellPromo from "../components/SellPromo";

const CONDS = ["excellent", "good", "fair", "needs_work"];
const TITLES = ["clean", "rebuilt", "salvage", "lien"];
const F = "bg-slate-900 border-slate-800 mt-1.5";

export default function SellCar() {
  const nav = useNavigate();
  const [form, setForm] = useState({ make: "", model: "", year: 2021, trim: "", mileage: 60000, asking_price: 15000,
    condition: "good", title_status: "clean", description: "", city: "", photos: [], features: "", deposit_amount: 500, vin: "" });
  const [busy, setBusy] = useState(false);
  const [vinRep, setVinRep] = useState(null);
  const [step, setStep] = useState("offer");
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fromOffer = (o) => { setForm((f) => ({ ...f, year: o.year, make: o.make, model: o.model, trim: o.trim, mileage: o.mileage, condition: o.condition, title_status: o.title_status, city: o.city, asking_price: o.asking_price })); setStep("form"); window.scrollTo({ top: 0, behavior: "smooth" }); };

  useEffect(() => {
    const v = form.vin.trim().toUpperCase();
    if (v.length !== 17) { setVinRep(null); return; }
    const t = setTimeout(() => {
      http.get(`/sales/vin/${v}?make=${encodeURIComponent(form.make)}&model=${encodeURIComponent(form.model)}&year=${Number(form.year) || 0}`)
        .then(({ data }) => setVinRep(data)).catch(() => setVinRep(null));
    }, 500);
    return () => clearTimeout(t);
  }, [form.vin, form.make, form.model, form.year]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.photos.length === 0) { toast.error("Add at least one photo"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sales", { ...form, year: Number(form.year), mileage: Number(form.mileage),
        asking_price: Number(form.asking_price), deposit_amount: Number(form.deposit_amount), vin: form.vin.trim() || null,
        features: form.features.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Your car is listed for sale");
      nav(`/buy/${data.listing_id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Check the form and try again");
    } finally { setBusy(false); }
  };

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-8 py-10" data-testid="sell-car-page">
      <div className="mb-8 rise">
        <p className="font-mono text-xs tracking-widest uppercase text-amber-400 mb-1">// Sell your car</p>
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Cash out. Zero haggling in your inbox.</h1>
        <p className="text-slate-400 mt-2">Get an instant cash offer first, or list it yourself — buyers message you in-app and reserve with a refundable deposit. <span className="text-amber-300">Accepted offers are guaranteed and pickup is free.</span></p>
      </div>
      {step === "offer" && <div className="mb-8 rise rise-1"><SellPromo /></div>}
      {step === "offer" ? <InstantOffer onListInstead={fromOffer} onSkip={() => setStep("form")} /> : (
      <form onSubmit={submit} className="glass rounded-3xl p-8 space-y-5 rise rise-1">
        <div className="flex items-center justify-between"><p className="font-mono text-xs tracking-widest uppercase text-amber-400">Step 2 · Listing details</p><button type="button" onClick={() => setStep("offer")} data-testid="back-to-offer-btn" className="text-xs text-slate-400 hover:text-white underline underline-offset-4">← Back to instant offer</button></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Make</Label><Input required value={form.make} onChange={(e) => upd("make", e.target.value)} data-testid="sell-make" className={F} /></div>
          <div><Label>Model</Label><Input required value={form.model} onChange={(e) => upd("model", e.target.value)} data-testid="sell-model" className={F} /></div>
          <div><Label>Year</Label><Input type="number" required value={form.year} onChange={(e) => upd("year", e.target.value)} data-testid="sell-year" className={F} /></div>
          <div><Label>Trim</Label><Input value={form.trim} onChange={(e) => upd("trim", e.target.value)} data-testid="sell-trim" className={F} /></div>
          <div><Label>Mileage</Label><Input type="number" required value={form.mileage} onChange={(e) => upd("mileage", e.target.value)} data-testid="sell-mileage" className={F} /></div>
          <div><Label>City</Label><Input required value={form.city} onChange={(e) => upd("city", e.target.value)} data-testid="sell-city" className={F} /></div>
          <div><Label>Condition</Label>
            <Select value={form.condition} onValueChange={(v) => upd("condition", v)}>
              <SelectTrigger data-testid="sell-condition" className={F}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#131722] border-slate-800">{CONDS.map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Title status</Label>
            <Select value={form.title_status} onValueChange={(v) => upd("title_status", v)}>
              <SelectTrigger data-testid="sell-title" className={F}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#131722] border-slate-800">{TITLES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <div>
          <Label className="flex items-center gap-2">VIN <span className="text-slate-500 font-normal text-xs">(17 characters · verified against the NHTSA database)</span></Label>
          <Input value={form.vin} maxLength={17} onChange={(e) => upd("vin", e.target.value.toUpperCase())} data-testid="sell-vin" placeholder="1HGCM82633A004352" className={`${F} font-mono tracking-widest`} />
          {vinRep && (
            <div className="mt-2 flex items-center gap-3 flex-wrap" data-testid="sell-vin-result">
              <VinBadge report={vinRep} />
              {vinRep.decoded?.make && <span className="text-xs text-slate-400">Decodes to {vinRep.decoded.year} {vinRep.decoded.make} {vinRep.decoded.model}{vinRep.decoded.body && ` · ${vinRep.decoded.body}`}</span>}
              {vinRep.status === "mismatch" && <button type="button" data-testid="sell-vin-apply" onClick={() => setForm((f) => ({ ...f, make: vinRep.decoded.make, model: vinRep.decoded.model || f.model, year: vinRep.decoded.year || f.year }))} className="text-xs text-cyan-400 hover:text-cyan-300 underline">Use decoded details</button>}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-xl bg-amber-500/5 border border-amber-500/30">
          <div><Label className="text-amber-300">Asking price (USD)</Label><Input type="number" required min={1} value={form.asking_price} onChange={(e) => upd("asking_price", e.target.value)} data-testid="sell-price" className={F} /></div>
          <div><Label>Refundable reserve deposit (USD)</Label><Input type="number" min={100} max={5000} value={form.deposit_amount} onChange={(e) => upd("deposit_amount", e.target.value)} data-testid="sell-deposit" className={F} /><p className="text-[11px] text-slate-500 mt-1">Holds the car for a serious buyer. $100–$5,000.</p></div>
        </div>
        <div><Label>Description</Label><Textarea required minLength={10} value={form.description} onChange={(e) => upd("description", e.target.value)} data-testid="sell-description" placeholder="Service history, why you're selling, known issues…" className={`${F} min-h-[110px]`} /></div>
        <div><Label>Photos</Label><div className="mt-1.5"><PhotoUploader photos={form.photos} onChange={(p) => upd("photos", p)} /></div></div>
        <div><Label>Features (comma-separated)</Label><Input value={form.features} onChange={(e) => upd("features", e.target.value)} data-testid="sell-features" className={F} placeholder="New tires, CarPlay, One owner" /></div>
        <Button type="submit" disabled={busy} data-testid="sell-submit" className="w-full h-12 bg-amber-500 text-slate-950 hover:bg-amber-400 font-semibold">
          {busy ? "Publishing…" : "List for sale"}
        </Button>
      </form>
      )}
      <div className="mt-10"><AutoCareCta context="before you sell" /></div>
    </main>
  );
}
